// src/components/IdleTimerProvider.js
import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../App';
import '../stylings/IdleTimerModal.css';

const IdleTimerProvider = ({ children, timeout = 60 * 1000, warningTime = 15 * 1000 }) => {
  const { logout } = useAuth();
  const [isWarningModalOpen, setIsWarningModalOpen] = useState(false);
  const [remainingTime, setRemainingTime] = useState(0);
  
  // Use refs instead of state to avoid re-renders
  const timerRef = useRef(null);
  const warningTimerRef = useRef(null);
  const countdownIntervalRef = useRef(null);
  const lastActivityRef = useRef(Date.now());
  
  // Function to handle user activity - no state updates here
  const handleUserActivity = () => {
    // Update the ref and sessionStorage without triggering re-renders
    lastActivityRef.current = Date.now();
    sessionStorage.setItem('lastActivity', Date.now().toString());
    
    // Reset timers
    if (timerRef.current) clearTimeout(timerRef.current);
    if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
    if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);
    
    // Close warning modal if open
    if (isWarningModalOpen) {
      setIsWarningModalOpen(false);
    }
    
    // Set new warning timer
    warningTimerRef.current = setTimeout(() => {
      console.log('Warning timer triggered');
      setIsWarningModalOpen(true);
      setRemainingTime(Math.floor(warningTime / 1000));
      
      // Start countdown
      countdownIntervalRef.current = setInterval(() => {
        setRemainingTime(prev => {
          if (prev <= 1) {
            clearInterval(countdownIntervalRef.current);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      
      // Set logout timer
      timerRef.current = setTimeout(() => {
        console.log('Logout timer triggered');
        logout();
      }, warningTime);
    }, timeout - warningTime);
  };
  
  // Set up the activity tracking once on mount
  useEffect(() => {
    console.log('IdleTimerProvider initialized');
    
    // Throttling mechanism
    let lastCallTime = 0;
    const throttleDelay = 5000; // Only process every 5 seconds
    
    const throttledActivityHandler = () => {
      const now = Date.now();
      if (now - lastCallTime > throttleDelay) {
        lastCallTime = now;
        console.log('Activity detected (throttled)');
        handleUserActivity();
      }
    };
    
    // Add event listeners
    const events = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart', 'click'];
    events.forEach(event => {
      window.addEventListener(event, throttledActivityHandler);
    });
    
    // Initial setup
    handleUserActivity();
    
    // Cleanup
    return () => {
      console.log('IdleTimerProvider cleanup');
      events.forEach(event => {
        window.removeEventListener(event, throttledActivityHandler);
      });
      
      if (timerRef.current) clearTimeout(timerRef.current);
      if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
      if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);
    };
  }, []); // Empty dependency array - only run on mount and unmount
  
  // Stay active button handler
  const handleStayActive = () => {
    console.log('Stay active clicked');
    handleUserActivity();
  };
  
  // Logout button handler
  const handleLogout = () => {
    console.log('Manual logout clicked');
    logout();
  };
  
  return (
    <>
      {children}
      
      {isWarningModalOpen && (
        <div className="idle-timer-modal-overlay">
          <div className="idle-timer-modal">
            <div className="idle-timer-modal-header">
              <h2>Session Timeout Warning</h2>
            </div>
            <div className="idle-timer-modal-body">
                <p>Your session will expire after 10 minutes of inactivity.</p>
                <p>You will be logged out in <span className="countdown">{remainingTime}</span> seconds.</p>
            </div>
            <div className="idle-timer-modal-footer">
              <button 
                className="stay-active-btn" 
                onClick={handleStayActive}
              >
                Stay Active
              </button>
              <button 
                className="logout-btn" 
                onClick={handleLogout}
              >
                Logout Now
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default IdleTimerProvider;